import { AIResultStatus } from '@zocks-communications/client-sdk-js'
import { type AiResult, type StructuredSummaryResultType } from '../../../../../../../models/AiResult'

export enum CalendarProvider {
  GOOGLE = 'google',
  OUTLOOK = 'outlook',
}

export interface FutureMeetingProps {
  name: string
  date: string | null
}

const getGoogleFormattedDate = (dateToFormat: Date) => {
  const isoString = dateToFormat.toISOString()
  const year = isoString.slice(0, 4)
  const month = isoString.slice(5, 7)
  const day = isoString.slice(8, 10)
  const hours = isoString.slice(11, 13)
  const minutes = isoString.slice(14, 16)
  const seconds = isoString.slice(17, 19)
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`
}

const getOutlookFormattedDate = (dateToFormat: Date) => {
  return dateToFormat.toISOString()
}

export const generateCalendarDeepLink = (provider: CalendarProvider, title: string, startDate: Date, endDate: Date, emails: string[]) => {
  const encodedTitle = encodeURIComponent(title)
  const encodedEmails = emails.map((email) => encodeURIComponent(email)).join(',')

  switch (provider) {
    case CalendarProvider.GOOGLE:
      return `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodedTitle}&dates=${getGoogleFormattedDate(startDate)}/${getGoogleFormattedDate(
        endDate,
      )}&add=${encodedEmails}`
    case CalendarProvider.OUTLOOK:
      return `https://outlook.office.com/calendar/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${encodedTitle}&to=${encodedEmails}&startdt=${getOutlookFormattedDate(
        startDate,
      )}&enddt=${getOutlookFormattedDate(endDate)}`
    default:
      throw new Error(`Unsupported calendar provider: ${provider}`)
  }
}

export const hasPresetFutureMeetings = (aiResult: AiResult): boolean => {
  if (aiResult.status === AIResultStatus.NO_RESULT) {
    return false
  }
  const result = aiResult.result
  return result != null && result.type === 'structuredSummary' && normalizeFutureMeetings(result.result).length > 0
}

export const normalizeFutureMeetings = (result: StructuredSummaryResultType): FutureMeetingProps[] => {
  if (Array.isArray(result)) {
    return result.flatMap((item) =>
      typeof item === 'object' && item !== null && !Array.isArray(item) && typeof item.name === 'string'
        ? [{ name: item.name, date: typeof item.date === 'string' ? item.date : null }]
        : [],
    )
  }

  if (result && typeof result === 'object') {
    return Object.entries(result).flatMap(([name, date]) => (typeof date === 'string' ? [{ name, date }] : []))
  }

  return []
}
